import { useMemo } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { SubPageHeader } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useVacationModuleAccess } from '@/features/Vacations/hooks/useVacationModuleAccess';
import { useManagerSuggestions } from '@/features/Vacations/hooks/data/useManagerSuggestions';
import { useCollaboratorSelfQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorSelfQuery';
import { formatDayOffDates } from '../../utils/formatDayOffDates';
import { BIRTHDAY_ISSUE_LABELS, nextBirthdayIso, type DayOffRequest, type DpRequest } from '../../../../../shared/dpRequests';
import { DayOffRequestForm } from '../../components/DayOffRequestForm';
import { DayOffRules } from '../../components/DayOffRules';
import { MyDayOffsSection } from '../../components/MyDayOffsSection';
import { useMyDayOffStatusQuery, useMyDpRequestsQuery } from '../../hooks/data/useDpRequestQueries';
import { todayIsoDate } from '../../utils/dayOffPolicy';
import { DP_REQUESTS_PATH, TICKET_PATH } from '../../routes';

const INVALID_DAY_SUBJECT = 'Day-off em dia inválido';

const ACTIVE_STATUSES = ['pending_manager', 'approved'];

function alreadyRequestedYear(requests: DpRequest[] | undefined, year: string): DayOffRequest | null {
    return (
        (requests ?? []).find(
            (request): request is DayOffRequest =>
                request.type === 'day_off' && ACTIVE_STATUSES.includes(request.status) && request.days.some(day => day.date.startsWith(year))
        ) ?? null
    );
}

export function DayOffPage() {
    const { user } = useAuth();
    const email = user?.email ?? null;
    const collaboratorId = user?.collaboratorId ?? '';
    const { canSeeModule, isLoading: accessLoading } = useVacationModuleAccess();
    const selfQuery = useCollaboratorSelfQuery(canSeeModule ? collaboratorId : '');
    const mineQuery = useMyDpRequestsQuery(email);
    const statusQuery = useMyDayOffStatusQuery(canSeeModule);
    const suggestions = useManagerSuggestions(collaboratorId || null);

    const todayIso = todayIsoDate();
    const birthday = useMemo(
        () => nextBirthdayIso((selfQuery.data as { birthDate?: unknown } | null)?.birthDate, todayIso),
        [selfQuery.data, todayIso]
    );
    const year = birthday?.slice(0, 4) ?? '';
    const existing = useMemo(() => (year ? alreadyRequestedYear(mineQuery.data, year) : null), [mineQuery.data, year]);

    const header = (
        <SubPageHeader
            backPlacement="end"
            backTo={DP_REQUESTS_PATH}
            icon={<CalendarDaysIcon className="h-6 w-6" aria-hidden="true" />}
            title="Day-off de aniversário"
            description="Uma folga no dia do seu aniversário, uma vez por ano. O gestor Raro e o cliente aprovam, e o DP lança no ponto."
        />
    );

    if (accessLoading || (canSeeModule && (selfQuery.isLoading || mineQuery.isLoading || statusQuery.isLoading))) {
        return (
            <div className="mx-auto max-w-3xl space-y-6">
                {header}
                <div className="h-72 animate-pulse rounded-2xl bg-raro-gray-100" />
            </div>
        );
    }

    if (!canSeeModule) {
        return (
            <div className="mx-auto max-w-3xl space-y-6">
                {header}
                <p className="rounded-2xl border border-raro-gray-200 bg-raro-gray-50 px-5 py-4 text-sm text-raro-gray-700">
                    O day-off é exclusivo para colaboradores CLT.
                </p>
            </div>
        );
    }

    if (!birthday) {
        return (
            <div className="mx-auto max-w-3xl space-y-6">
                {header}
                <p className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                    Não encontramos sua data de nascimento no cadastro. Procure o DP para atualizá-la antes de pedir o day-off.
                </p>
            </div>
        );
    }

    const [y, m, d] = birthday.split('-');
    const weekday = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('pt-BR', { weekday: 'long' });

    return (
        <div className="mx-auto max-w-3xl space-y-8">
            {header}

            <div className="rounded-2xl border border-primary-100 bg-primary-50 px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Seu day-off</p>
                <p className="mt-1 font-montserrat text-lg font-semibold text-raro-gray-900">
                    {formatDayOffDates([{ date: birthday, period: 'full' }])[0]}
                </p>
                <p className="text-sm capitalize text-raro-gray-600">{weekday}</p>
            </div>

            <DayOffRules />

            {statusQuery.data?.issue ? (
                <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                    <p>
                        Seu day-off cai em um <strong>dia inválido</strong> ({BIRTHDAY_ISSUE_LABELS[statusQuery.data.issue]}). Esse caso precisa ser verificado com o DP.
                    </p>
                    <Link
                        to={`${TICKET_PATH}?assunto=${encodeURIComponent(INVALID_DAY_SUBJECT)}`}
                        className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
                    >
                        Falar com o DP
                    </Link>
                </div>
            ) : existing ? (
                <p className="rounded-2xl border border-raro-gray-200 bg-raro-gray-50 px-5 py-4 text-sm text-raro-gray-700">
                    Você já pediu o day-off de {year}. Veja o status abaixo. Só é possível um por ano.
                </p>
            ) : (
                <DayOffRequestForm suggestions={suggestions.suggestions} disabled={false} />
            )}

            <MyDayOffsSection requests={(mineQuery.data ?? []).filter((request): request is DayOffRequest => request.type === 'day_off')} />
        </div>
    );
}

export default DayOffPage;
